/**
 * ClimateProfileChart — monthly climate visual driven by
 * report.timeSeries.
 *
 * The data was previously consumed only by the simulation tooling;
 * the rendered report had no view of it. This component plots:
 *
 *   - Avg temperature (Area, left axis, °C)
 *   - Wet-bulb temperature (Line, left axis, °C) — the cooling-tower
 *     design driver; highlighted because it's the single most
 *     load-bearing climate input for cooling-cap sizing.
 *   - Precipitation (Bar, right axis, mm) — water-availability
 *     surrogate.
 *
 * GHI per month would be a richer right-axis bar but the existing
 * TimeSeriesData shape doesn't carry monthly GHI; precipitation is
 * what we have.
 *
 * J6 — when `timeSeries` is empty (stale/empty SED climate, same class as
 * the precip self-heal #16/#18 — repopulated on the next SED rebuild) the
 * chart renders an HONEST, compact, design-token placeholder rather than
 * returning null. This is NOT a fabricated chart: it tells the reader the
 * data is refreshing and what to do, so an empty climate slot is a visible,
 * actionable state instead of a silently-missing figure.
 */
import React from 'react';
import {
  ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine,
} from 'recharts';
import { ChartFrame } from '../../../ui/ChartFrame';
import { resolveReportChartPalette } from '../../../../lib/reportChartTheme';
import { resolveChartGridChrome } from '../../../../lib/reportChartGrid';
import { roleColor } from '../../../../lib/reportRoleColors';
import {
  CHART_FRAME_BOUNDS, CHART_PLOT_MARGIN, CHART_INK,
} from '../../../../lib/reportChartLayoutTokens';
import { SleekChartTooltip } from '../../sleek/SleekChartTooltip';
import type { ReportTheme } from '../../../../types/reportTheme';

const MONO = 'var(--report-body-font, inherit)';

interface Props {
  timeSeries: import('../../../../types/siteRag').TimeSeriesData[];
  theme: ReportTheme;
}

export const ClimateProfileChart: React.FC<Props> = ({ timeSeries, theme }) => {
  const ts = timeSeries;
  if (ts.length === 0) {
    // J6 — honest placeholder (no fabricated data). The monthly series is
    // persisted at gen-time (siteRagService → report.timeSeries from
    // sed.climate.monthlySummary); an empty slot means the SED climate was
    // stale/empty at generation. The precip self-heal (#16/#18) repopulates
    // monthlySummary on the next SED rebuild, so the actionable guidance is
    // to regenerate after a rebuild.
    return (
      <div
        className="text-xs italic px-3 py-3"
        // William QA 2026-06-28 — left accent rule removed report-wide
        style={{
          color: roleColor(theme, 'muted'),
          // William QA 2026-06-29 — chart backgrounds transparent; the empty-state
          // placeholder no longer paints a cream/accent box behind the chart slot.
          background: 'transparent',
          fontFamily: MONO,
        }}
        data-testid="climate-profile-placeholder"
      >
        Climate data refreshing – regenerate this report after the next SED rebuild
        to populate the monthly temperature, wet-bulb, and precipitation profile.
      </div>
    );
  }

  // P2.6 — theme palette via the single chart-theme adapter. Legacy
  // (no-preset) reports carry DEFAULT_THEME.chartPalette so the temp / wet-bulb
  // / precip series keep their existing colours; a preset / tenant palette
  // reskins. Indices 0-2 drive the three series; index 3 is the peak-temp line.
  const palette = resolveReportChartPalette(theme, 4);
  // Grid ink derived from the theme so it reads against the page background.
  // Byte-stable (#e5e7eb) on a white page.
  const { grid: gridColor } = resolveChartGridChrome(theme);
  // Axis ink reads the theme muted tone so the ticks/labels match the page.
  const tickInk = roleColor(theme, 'muted');

  // K4 — the mean wet-bulb temperature is the single cooling-tower design
  // driver; the reference page draws it as a horizontal dotted datum across the
  // monthly profile. This is a REAL computed mean of the persisted monthly
  // wet-bulb series (not a fabricated figure), so it self-heals with the data.
  const wetBulbValues = ts
    .map((d) => d.wetBulbTemp)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  const meanWetBulb =
    wetBulbValues.length > 0
      ? Math.round((wetBulbValues.reduce((s, v) => s + v, 0) / wetBulbValues.length) * 10) / 10
      : null;
  // K4 — `peakTemp` is a guaranteed field on every monthly row; plotting it as
  // a faint upper-envelope line (alongside the avg-temp area + wet-bulb line)
  // gives the figure the reference's dry-bulb high/avg + wet-bulb density. No
  // new data — the series already rides on report.timeSeries.
  const hasPeak = ts.some((d) => typeof d.peakTemp === 'number' && Number.isFinite(d.peakTemp));

  // BUG #6 — NEITHER YAxis previously carried an explicit `domain`. recharts
  // then auto-scales each axis from min→max of its bound series, so ONE outlier
  // month (a single extreme peak temp, or a single very-wet month) stretched
  // the axis and compressed every other point into a near-flat row of dots —
  // the "chart missing data" defect (the temperature area/line + precip bars
  // were present but crushed against the baseline). We fix this WITHOUT clipping
  // by deriving explicit, robust domains from the FINITE values only, with a
  // small symmetric pad on the temperature axis and a [0, max·1.1] precip axis.
  //
  // We also sanitise the plotted rows so a non-finite numeric (NaN / undefined)
  // becomes `null` (which recharts skips per-point) instead of poisoning the
  // axis auto-scale or drawing a 0-spike — the month label is always kept so
  // the X axis never loses a tick. NaN/undefined precipitation is honest data
  // absence, not zero, so it is omitted rather than plotted as a 0 bar.
  const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  const num = (v: unknown): number | null => (finite(v) ? v : null);
  const plotData = ts.map((d) => ({
    month: d.month,
    avgTemp: num(d.avgTemp),
    peakTemp: num(d.peakTemp),
    wetBulbTemp: num(d.wetBulbTemp),
    precipitation: num(d.precipitation),
  }));

  // Temperature axis (left) — span the lowest plotted temp (avg / wet-bulb)
  // to the highest (peak / avg), padded ~2°C each side so the lines/area sit
  // off the frame edges. Falls back to a sane room-temperature window if a
  // report somehow carries no finite temperatures (never clips real data).
  const tempValues = plotData.flatMap((d) =>
    [d.avgTemp, d.wetBulbTemp, d.peakTemp].filter(finite),
  );
  const tempPad = 2;
  const tempDomain: [number, number] = tempValues.length > 0
    ? [
        Math.floor(Math.min(...tempValues) - tempPad),
        Math.ceil(Math.max(...tempValues) + tempPad),
      ]
    : [0, 40];

  // Precip axis (right) — always anchored at 0 (precipitation has a true zero)
  // up to the wettest month + 10% headroom so the tallest bar is not flush with
  // the frame. A floor of 10mm keeps the axis readable when every month is dry.
  const precipValues = plotData.map((d) => d.precipitation).filter(finite);
  const precipMax = precipValues.length > 0 ? Math.max(...precipValues) : 0;
  const precipDomain: [number, number] = [0, Math.max(10, Math.ceil(precipMax * 1.1))];

  return (
    <div
      className="space-y-2"
      data-testid="climate-profile-chart"
    >
      {/* RRQ DEDUPE — the "Monthly Climate Profile" heading is owned by the
          SECTION wrapper (SiteRagReportView climateSevereWeather sub-heading,
          theme-styled <h3>). The internal heading that previously lived here
          duplicated it in the printed report, so it is removed; this component
          renders ONLY the chart canvas. */}
      <div className="w-full" data-testid="climate-profile-canvas" data-export-capture="" data-export-id="climate-profile-canvas" data-export-title="Climate Profile">
        <ChartFrame
          exportId="climate-profile-canvas"
          traceName="report:climate-profile"
          minWidth={100}
          aspect={CHART_FRAME_BOUNDS.combo.aspect}
          minHeight={CHART_FRAME_BOUNDS.combo.minHeight}
          maxHeight={CHART_FRAME_BOUNDS.combo.maxHeight}
          legibilityFloor={CHART_FRAME_BOUNDS.combo.legibilityFloor}
        >
          <ComposedChart data={plotData} margin={CHART_PLOT_MARGIN.combo}>
            {/* K4 — precip bars READ AS the field (the temperature lines are the
                 figure's headline), so the bars sit behind a faint hairline grid
                 and the lines draw over them. Horizontal grid only — vertical
                 month gridlines would clutter the dense 12-point axis. */}
            <CartesianGrid strokeDasharray="2 4" stroke={gridColor} vertical={false} />
            {/* VIEWER↔PDF PARITY — force EVERY month tick. recharts' XAxis
                 default `interval="preserveEnd"` auto-DROPS ticks once the
                 labels would collide, so at the fixed ~700px (or narrower
                 2-col) print column the axis silently truncated to ~4 months
                 even though all 12 bars/points were drawn. `interval={0}` +
                 `minTickGap={0}` pins all 12; the smaller, angled tick keeps
                 them legible without overlap at the narrow print width while
                 staying byte-equivalent in spirit at wide widths. */}
            <XAxis
              dataKey="month"
              interval={0}
              minTickGap={0}
              tickMargin={6}
              height={32}
              angle={-35}
              textAnchor="end"
              tick={{ fontSize: CHART_INK.denseTickFontSize, fill: tickInk }}
              tickLine={false}
            />
            {/* BUG #6 — explicit, robust domains (computed above from FINITE
                 values only) so a single outlier month can no longer auto-scale
                 the axis and compress every other point into dots. `allowDataOverflow`
                 stays false so nothing real is clipped — the domain already
                 encloses the padded min/max of the plotted series. */}
            <YAxis
              yAxisId="temp"
              orientation="left"
              domain={tempDomain}
              label={{ value: '°C', angle: -90, position: 'insideLeft', fontSize: CHART_INK.axisLabelFontSize, fill: tickInk }}
              tick={{ fontSize: CHART_INK.axisTickFontSize, fill: tickInk }}
              tickLine={false}
            />
            <YAxis
              yAxisId="precip"
              orientation="right"
              domain={precipDomain}
              label={{ value: 'mm precip.', angle: 90, position: 'insideRight', fontSize: CHART_INK.axisLabelFontSize, fill: tickInk }}
              tick={{ fontSize: CHART_INK.axisTickFontSize, fill: tickInk }}
              tickLine={false}
            />
            <Tooltip content={<SleekChartTooltip />} />
            <Legend wrapperStyle={{ fontSize: CHART_INK.legendFontSize }} iconType="plainline" />
            {/* Precipitation field — water-availability surrogate, drawn first
                 (behind) as faint columns so the temperature lines read over it. */}
            <Bar
              yAxisId="precip"
              isAnimationActive={false}
              dataKey="precipitation"
              name="Precipitation"
              fill={palette[2]}
              fillOpacity={0.32}
              barSize={14}
              radius={[1, 1, 0, 0]}
            />
            {/* Avg dry-bulb — the soft temperature band. */}
            <Area
              yAxisId="temp"
              isAnimationActive={false}
              type="monotone"
              dataKey="avgTemp"
              name="Avg temp"
              fill={palette[0]}
              fillOpacity={0.18}
              stroke={palette[0]}
              strokeWidth={1.4}
              dot={false}
            />
            {/* K4 — peak dry-bulb high as a faint dashed upper envelope; same
                 hue as the avg band so the two read as a dry-bulb high/avg pair.
                 Guarded so a series of missing peaks never draws a flat line. */}
            {hasPeak && (
              <Line
                yAxisId="temp"
                type="monotone"
                isAnimationActive={false}
                dataKey="peakTemp"
                name="Peak temp"
                stroke={palette[3]}
                strokeWidth={1.2}
                strokeDasharray="4 3"
                dot={false}
                opacity={0.85}
              />
            )}
            {/* Wet-bulb — the cooling-tower design driver, the figure's headline
                 series: full-weight stroke + dotted vertex markers. */}
            <Line
              yAxisId="temp"
              type="monotone"
              isAnimationActive={false}
              dataKey="wetBulbTemp"
              name="Wet-bulb"
              stroke={palette[1]}
              strokeWidth={2.2}
              dot={{ r: CHART_INK.lineDotRadius, fill: palette[1], strokeWidth: 0 }}
              activeDot={{ r: CHART_INK.lineDotRadius + 1.5 }}
            />
            {/* K4 — mean-wet-bulb datum (the reference's dotted horizontal
                 reference line). A REAL computed mean of the plotted series, so
                 it is honest + self-healing. Labelled with the °C value. */}
            {meanWetBulb != null && (
              <ReferenceLine
                yAxisId="temp"
                y={meanWetBulb}
                stroke={palette[1]}
                strokeDasharray="2 4"
                strokeOpacity={0.7}
                label={{
                  // DEFECT 5 — anchor the datum label INSIDE-TOP-LEFT (was
                  // 'right'), which collided with the right Y-axis "mm precip."
                  // title. Inside-left keeps it on the datum line, clear of the
                  // right axis, with no plot-margin change.
                  value: `12-mo mean WB ${meanWetBulb}°C`,
                  position: 'insideTopLeft',
                  fontSize: CHART_INK.denseTickFontSize,
                  fill: roleColor(theme, 'muted'),
                }}
              />
            )}
          </ComposedChart>
        </ChartFrame>
      </div>
    </div>
  );
};
